/**
 * Распознавание штрихкода с фото (Code 128 и др.) для поиска по номеру поступления.
 * Несколько стратегий: BarcodeDetector (где есть), ZXing с подсказками, масштаб/грейскейл для мелкого кода.
 */
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

const DEBUG_KEY = "DEBUG_SUPPLY_BARCODE";

function debug(...args) {
  try {
    if (
      process.env.NODE_ENV === "development" ||
      localStorage.getItem(DEBUG_KEY) === "1"
    ) {
      // eslint-disable-next-line no-console
      console.log("[supplyBarcode]", ...args);
    }
  } catch {
    /* ignore */
  }
}

function loadImageFromObjectUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("IMAGE_LOAD_FAILED"));
    img.src = url;
  });
}

function getSourceSize(source) {
  if (source instanceof HTMLImageElement) {
    return {
      w: source.naturalWidth || source.width,
      h: source.naturalHeight || source.height,
    };
  }
  return { w: source.width, h: source.height };
}

function drawImageToCanvas(source, scale = 1) {
  const { w: sw, h: sh } = getSourceSize(source);
  const w = Math.max(1, Math.round(sw * scale));
  const h = Math.max(1, Math.round(sh * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = scale !== 1;
  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

/** Уменьшает очень большие фото, чтобы ускорить декодирование. */
function fitCanvasMax(canvas, maxDim) {
  const w = canvas.width;
  const h = canvas.height;
  const m = Math.max(w, h);
  if (m <= maxDim) {
    return canvas;
  }
  const scale = maxDim / m;
  const out = document.createElement("canvas");
  out.width = Math.round(w * scale);
  out.height = Math.round(h * scale);
  out.getContext("2d").drawImage(canvas, 0, 0, out.width, out.height);
  return out;
}

function toGrayscaleCanvas(canvas) {
  const ctx = canvas.getContext("2d");
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imgData.data;
  for (let i = 0; i < d.length; i += 4) {
    const y = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    d[i] = y;
    d[i + 1] = y;
    d[i + 2] = y;
  }
  ctx.putImageData(imgData, 0, 0);
  return canvas;
}

function invertCanvas(canvas) {
  const ctx = canvas.getContext("2d");
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imgData.data;
  for (let i = 0; i < d.length; i += 4) {
    d[i] = 255 - d[i];
    d[i + 1] = 255 - d[i + 1];
    d[i + 2] = 255 - d[i + 2];
  }
  ctx.putImageData(imgData, 0, 0);
  return canvas;
}

async function tryBarcodeDetector(img) {
  if (typeof window === "undefined" || !("BarcodeDetector" in window)) {
    return null;
  }
  try {
    const Detector = window.BarcodeDetector;
    const detector = new Detector({
      formats: ["code_128", "code_39", "ean_13", "ean_8", "qr_code"],
    });
    let bitmap = null;
    try {
      if (typeof createImageBitmap === "function") {
        bitmap = await createImageBitmap(img);
      }
    } catch (e) {
      debug("createImageBitmap failed", e);
    }
    const source = bitmap || img;
    const codes = await detector.detect(source);
    if (codes.length) {
      debug("BarcodeDetector ok", codes[0].format, codes[0].rawValue);
      return codes[0].rawValue;
    }
  } catch (e) {
    debug("BarcodeDetector error", e);
  }
  return null;
}

function buildZxingReaders() {
  const hints128 = new Map();
  hints128.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.ITF,
  ]);
  hints128.set(DecodeHintType.TRY_HARDER, true);

  const hintsWide = new Map();
  hintsWide.set(DecodeHintType.TRY_HARDER, true);

  return [
    new BrowserMultiFormatReader(hints128),
    new BrowserMultiFormatReader(hintsWide),
  ];
}

async function tryDecodeCanvas(reader, canvas) {
  return reader.decodeFromCanvas(canvas);
}

/**
 * Собирает варианты canvas: масштаб, грейскейл, инверсия (часто помогает при тёмном фоне).
 */
function buildCanvasVariants(img) {
  const { w: nw, h: nh } = getSourceSize(img);
  const maxSide = Math.max(nw, nh);
  const core = [];

  core.push({ label: "base", canvas: drawImageToCanvas(img, 1) });
  if (maxSide > 2048) {
    core.push({
      label: "fit2048",
      canvas: fitCanvasMax(drawImageToCanvas(img, 1), 2048),
    });
  }
  if (maxSide < 900) {
    core.push({ label: "scale2x", canvas: drawImageToCanvas(img, 2) });
  }
  if (maxSide < 500) {
    core.push({ label: "scale3x", canvas: drawImageToCanvas(img, 3) });
  }

  const out = [];
  for (const v of core) {
    out.push(v);
    const g = document.createElement("canvas");
    g.width = v.canvas.width;
    g.height = v.canvas.height;
    g.getContext("2d").drawImage(v.canvas, 0, 0);
    toGrayscaleCanvas(g);
    out.push({ label: `${v.label}+gray`, canvas: g });

    const inv = document.createElement("canvas");
    inv.width = v.canvas.width;
    inv.height = v.canvas.height;
    inv.getContext("2d").drawImage(v.canvas, 0, 0);
    invertCanvas(inv);
    out.push({ label: `${v.label}+inv`, canvas: inv });
  }
  return out;
}

async function tryZxingOnImageAndCanvases(img) {
  const readers = buildZxingReaders();
  const url = img.src;

  for (let r = 0; r < readers.length; r++) {
    const reader = readers[r];
    try {
      const result = await reader.decodeFromImageElement(img);
      debug("ZXing decodeFromImageElement ok", r, result.getText());
      return result.getText();
    } catch (e) {
      debug("ZXing decodeFromImageElement fail", r, e?.name || e);
    }
    if (url && url.startsWith("blob:")) {
      try {
        const result = await reader.decodeFromImageUrl(url);
        debug("ZXing decodeFromImageUrl ok", r, result.getText());
        return result.getText();
      } catch (e) {
        debug("ZXing decodeFromImageUrl fail", r, e?.name || e);
      }
    }
  }

  const variants = buildCanvasVariants(img);
  for (const { label, canvas } of variants) {
    for (let r = 0; r < readers.length; r++) {
      const reader = readers[r];
      try {
        const result = await tryDecodeCanvas(reader, canvas);
        debug("ZXing canvas ok", label, r, result.getText());
        return result.getText();
      } catch (e) {
        debug("ZXing canvas fail", label, r, e?.name || e);
      }
    }
  }

  return null;
}

/**
 * @param {File} file — снимок с камеры или файл с диска (Safari / десктоп)
 * @returns {Promise<string|null>} сырая строка штрихкода или null
 */
export async function scanBarcodeFromImageFile(file) {
  if (!file || !file.type.startsWith("image/")) {
    debug("skip: not an image", file?.type);
    return null;
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImageFromObjectUrl(url);
    debug("image loaded", img.naturalWidth, img.naturalHeight, file.type);

    const bd = await tryBarcodeDetector(img);
    if (bd) {
      return bd;
    }

    const zx = await tryZxingOnImageAndCanvases(img);
    return zx;
  } finally {
    URL.revokeObjectURL(url);
  }
}
